import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Sunrise, Store, BoxIcon } from "lucide-react";
import { HERO_BG, CLOSING_BG, chapters } from "../data/horizon";
import { INVASION_BG } from "../data/invasions";
import { visibleCategories } from "../utils/guideIndex";
type Section = {
  to: string;
  image: string;
  /** Logotipos: se muestran completos sobre fondo claro, sin recorte ni velo. */
  logo?: boolean;
  icon: BoxIcon;
  eyebrow: string;
  title: string;
  description: string;
  tags: string[];
  cta: string;
};
const MARKET_IMAGE = 'https://cdn.magicpatterns.com/uploads/kwMnQNjcrFY4iEUwUoebut/image.png';
const sections: Section[] = [{
  to: '/guia',
  image: INVASION_BG,
  icon: BookOpen,
  eyebrow: 'Cómo funciona hoy',
  title: 'Guía',
  description: 'Mecánicas, ítems, mapas, monstruos e invasiones del servidor, con dónde conseguir cada cosa.',
  tags: visibleCategories.slice(0, 3).map(c => c.title),
  cta: 'Abrir la guía'
}, {
  to: '/proyeccion',
  image: CLOSING_BG,
  icon: Sunrise,
  eyebrow: 'Hacia dónde vamos',
  title: 'Proyección',
  description: `La hoja de ruta del servidor en ${chapters.length} capítulos: progreso, mazmorras, prestigio, economía y comunidad.`,
  tags: ['Progreso', 'Economía', 'Comunidad'],
  cta: 'Ver la proyección'
}, {
  to: '/vanguard-market',
  image: MARKET_IMAGE,
  logo: true,
  icon: Store,
  eyebrow: 'Compra y vende',
  title: 'Vanguard Market',
  description: 'Aprende a comprar y vender ítems, recargar tu saldo y retirar USDT paso a paso.',
  tags: ['Comprar', 'Vender', 'Wallet'],
  cta: 'Aprender a usarlo'
}];
export function Home() {
  useEffect(() => {
    document.title = 'MU Vanguard · Guía, Proyección y Market';
  }, []);
  return <main className="relative overflow-hidden" data-id="element-3014" data-mp="23auaiu4yyc8v">
      <div className="absolute inset-x-0 top-0 h-[520px] bg-cover bg-center opacity-30" style={{
      backgroundImage: `url("${HERO_BG}")`
    }} aria-hidden="true" data-id="element-3015" data-mp="1z7xsp5n96k5z" />
      <div className="absolute inset-x-0 top-0 h-[520px] bg-[#050b14]/60" aria-hidden="true" data-id="element-3016" data-mp="1uap7ew6t600i" />
      <div className="absolute inset-x-0 top-[320px] bottom-0 bg-[#050b14]" aria-hidden="true" data-id="element-3017" data-mp="1kn3ewkc3k34q" />

      <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-24 sm:px-6 md:pb-24 md:pt-44 lg:px-8" data-id="element-3018" data-mp="1ojzgkj7o0a9i">
        <header className="mx-auto max-w-3xl text-center" data-id="element-3019" data-mp="1aow0o996zz6f">
          <p className="font-cinzel text-xs font-semibold text-[#D4AF37] md:text-base" data-id="element-3020" data-mp="1fmfe56y2g7xb">Centro de información</p>
          <h1 className="mt-2 font-cinzel text-[1.75rem] font-black leading-[1.1] tracking-tight text-white sm:text-5xl md:mt-3 md:text-6xl" data-id="element-3021" data-mp="23c09kpbeoxnh">
            Todo sobre <span className="text-[#D4AF37]" data-id="element-3022" data-mp="1tlx8s2y1mfel">MU Vanguard</span>
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-gray-300 md:mt-4 md:text-lg" data-id="element-3023" data-mp="1yki8m19dux2i">Cómo funciona el servidor hoy, hacia dónde vamos y cómo usar el Market. Elige por dónde empezar.</p>
        </header>

        <div className="mt-7 grid grid-cols-1 gap-3 md:mt-14 md:grid-cols-3 md:gap-6" data-id="element-3024" data-mp="2r1b3bmuc9rs4">
          {sections.map(s => <SectionCard key={s.to} s={s} data-id="element-3025" data-mp="fl0t5wm8l4x1" />)}
        </div>
      </div>
    </main>;
}
function SectionCard({
  s
}: {
  s: Section;
}) {
  const Icon = s.icon;
  return <Link to={s.to} className="group flex flex-row overflow-hidden rounded-2xl border border-white/10 bg-[#0A1628] transition-colors duration-200 hover:border-[#D4AF37]/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] md:flex-col md:rounded-3xl" data-id="element-3026" data-mp="akwkgsrklehd">
      <div className={`relative w-28 shrink-0 overflow-hidden sm:w-40 md:aspect-[16/10] md:w-full ${s.logo ? 'bg-white' : ''}`} data-id="element-3027" data-mp="3ssm08lzq4m48">
        <img src={s.image} alt={s.logo ? 'Vanguard market' : ''} className={`absolute inset-0 h-full w-full transition-transform duration-300 ease-out group-hover:scale-[1.03] ${s.logo ? 'object-contain p-3 md:p-10' : 'object-cover'}`} data-id="element-3028" data-mp="5nmp6whi28lm" />
        {!s.logo && <div className="absolute inset-0 bg-[#050b14]/35" aria-hidden="true" data-id="element-3029" data-mp="1h1i0fu5a9x7k" />}
        <span className={`${s.logo ? 'hidden' : 'flex'} absolute left-2.5 top-2.5 h-8 w-8 items-center justify-center rounded-xl bg-[#D4AF37] text-[#050b14] md:left-5 md:top-5 md:h-11 md:w-11 md:rounded-2xl`} data-id="element-3030" data-mp="1c4hv5lg9cyhi">
          <Icon className="h-4 w-4 md:h-5 md:w-5" aria-hidden="true" data-id="element-3031" data-mp="2ce15kqrmattw" />
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col p-4 sm:p-5 md:p-6" data-id="element-3032" data-mp="2hb17cphfpaf7">
        <p className="font-cinzel text-[11px] font-bold text-[#D4AF37] md:text-sm" data-id="element-3033" data-mp="2mboydb5nxr9f">{s.eyebrow}</p>
        <h2 className="mt-0.5 font-cinzel text-xl font-black leading-tight text-white sm:text-2xl md:mt-1.5 md:text-3xl" data-id="element-3034" data-mp="2qd0u7yf4r8q2">{s.title}</h2>
        <p className="mt-1.5 line-clamp-2 text-xs leading-snug text-gray-300 sm:text-sm md:mt-3 md:line-clamp-none md:text-base md:leading-relaxed" data-id="element-3035" data-mp="2vaj1zlhevhwh">{s.description}</p>
        <ul className="mt-4 hidden flex-wrap gap-2 md:flex" data-id="element-3036" data-mp="3526or930warm">
          {s.tags.map(t => <li key={t} className="rounded-full bg-white/[0.07] px-3 py-1 text-xs font-semibold text-gray-300" data-id="element-3037" data-mp="3a0qd1g539yn5">{t}</li>)}
        </ul>
        <div className="mt-auto pt-3 md:pt-6" data-id="element-3038" data-mp="3e1k1qbqcvhh3">
          <span className="inline-flex h-9 items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-4 font-cinzel text-xs font-bold text-[#050b14] whitespace-nowrap transition-colors duration-150 group-hover:bg-[#F3E5AB] md:h-12 md:px-7 md:text-sm" data-id="element-3039" data-mp="3e08xybwwu9pz">
            {s.cta}
            <ArrowRight className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-3040" data-mp="tetpv1fbin7b" />
          </span>
        </div>
      </div>
    </Link>;
}